import { isPartsObject } from '../engine/ticks.js';
const FIELDS = [
    ['month', 'months'],
    ['intercalary', 'intercalary'],
    ['season', 'seasons'],
    ['era', 'eras'],
];
/** One side through one remap: a key mapped to a key is replaced, to null removed, else kept. */
function remapSide(parts, remap) {
    if (!isPartsObject(remap))
        return { ok: true, parts };
    let out = parts;
    for (const [field, mapName] of FIELDS) {
        const key = out[field];
        const map = Object.hasOwn(remap, mapName)
            ? remap[mapName]
            : undefined;
        if (typeof key !== 'string' || !isPartsObject(map) || !Object.hasOwn(map, key))
            continue;
        const next = map[key];
        if (next === null)
            return { ok: false };
        if (typeof next === 'string')
            out = { ...out, [field]: next };
    }
    return { ok: true, parts: out };
}
/**
 * The input with its keys resolved through every remap of the chain, in order, as new objects
 * (the argument is never mutated); `key_removed` when a step maps one of its keys to null.
 */
export function applyRemaps(input, chain) {
    if (!isPartsObject(input))
        return { ok: true, input };
    const steps = Array.isArray(chain) ? chain : [];
    let lo = isPartsObject(input.loParts) ? { ...input.loParts } : input.loParts;
    let hi = isPartsObject(input.hiParts) ? { ...input.hiParts } : input.hiParts;
    for (const remap of steps) {
        if (isPartsObject(lo)) {
            const r = remapSide(lo, remap);
            if (!r.ok)
                return { ok: false, reason: 'key_removed' };
            lo = r.parts;
        }
        if (isPartsObject(hi)) {
            const r = remapSide(hi, remap);
            if (!r.ok)
                return { ok: false, reason: 'key_removed' };
            hi = r.parts;
        }
    }
    return {
        ok: true,
        input: { ...input, loParts: lo, hiParts: hi },
    };
}
/** The keys of a definition, by remap map: regular months, intercalary months, seasons, eras. */
function keysOf(definition) {
    const out = {
        months: new Set(),
        intercalary: new Set(),
        seasons: new Set(),
        eras: new Set(),
    };
    if (!isPartsObject(definition))
        return out;
    const list = (name) => {
        const value = definition[name];
        return Array.isArray(value) ? value.filter(isPartsObject) : [];
    };
    for (const m of list('months')) {
        if (typeof m.key === 'string')
            (m.intercalary === true ? out.intercalary : out.months).add(m.key);
    }
    for (const s of list('seasons'))
        if (typeof s.key === 'string')
            out.seasons.add(s.key);
    for (const e of list('eras'))
        if (typeof e.key === 'string')
            out.eras.add(e.key);
    return out;
}
/**
 * The remap from one definition to another: every key of `from` that `to` lacks (of the same kind)
 * maps to null, unless `renames` maps it to a key of `to`. A rename to a key `to` has is kept even
 * when `to` still has the old key (a swap).
 */
export function inferRemap(from, to, renames) {
    const a = keysOf(from);
    const b = keysOf(to);
    const remap = { months: {}, intercalary: {}, seasons: {}, eras: {} };
    for (const [, name] of FIELDS) {
        const into = remap[name];
        const given = isPartsObject(renames) && isPartsObject(renames[name])
            ? renames[name]
            : {};
        for (const key of a[name]) {
            const target = Object.hasOwn(given, key) ? given[key] : undefined;
            if (typeof target === 'string' && b[name].has(target)) {
                if (target !== key)
                    into[key] = target;
            }
            else if (!b[name].has(key)) {
                into[key] = null;
            }
        }
    }
    return remap;
}
//# sourceMappingURL=remap.js.map